import { useEffect, useState } from "react";
import api, { apiError, printAgent } from "@/lib/api";
import { toast } from "sonner";
import { Boxes, History, PackagePlus, Printer } from "lucide-react";
import Pagination from "@/components/panel/Pagination";
import usePaginatedList from "@/hooks/usePaginatedList";
import ProductPicker from "@/components/panel/pickers/ProductPicker";
import {
  Badge, Button, Card, CardBody, DataTable, Field, Input, PageHeader, Scale, SearchInput, Select, FOCUS_RING,
} from "@/components/ui-kit";
import { cn } from "@/lib/utils";
import { buildStockMovementHtml } from "@/lib/thermalDoc";

const TYPE_TONE = { entrada: "success", saida: "danger", ajuste: "info" };
const TYPE_LABEL = { entrada: "Entrada", saida: "Saída", ajuste: "Ajuste" };
const LOC_LABEL = { online: "Online", fisica: "Física" };

/* Referência da escala de estoque: 5 é o limite de "estoque baixo" usado no
   painel, então 10 põe esse limite exatamente na metade da escala. */
const SCALE_MAX = 10;

export default function Stock() {
  const [tab, setTab] = useState("saldo"); // saldo | movimentos
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [form, setForm] = useState({ product_id: "", variant_id: "", type: "entrada", location: "fisica", qty: 1, reason: "" });
  const [busy, setBusy] = useState(false);

  // A busca do topo vale para as duas abas e vai ao servidor.
  useEffect(() => {
    const t = setTimeout(() => setTerm(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const balance = usePaginatedList("/admin/products", { search: term || undefined });
  const movs = usePaginatedList("/admin/stock/movements", {
    search: term || undefined,
    type: typeFilter || undefined,
  });

  const load = () => { balance.refetch(); movs.refetch(); };

  // O picker já resolve produto + variação (e escolhe sozinho quando a variação
  // é única), então aqui só guardamos o resultado.
  const selectProduct = ({ product, variant }) => {
    setForm((f) => ({ ...f, product_id: product?.id || "", variant_id: variant?.id || "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_id || !form.variant_id) { toast.error("Selecione produto e variação"); return; }
    setBusy(true);
    try {
      await api.post("/admin/stock/movements", { ...form, qty: parseInt(form.qty) });
      toast.success("Movimentação registrada");
      setForm({ ...form, qty: 1, reason: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const balanceRows = balance.items.flatMap((p) =>
    (p.variants || []).map((v, idx) => ({ p, v, first: idx === 0 })));

  const tabBtn = (t) => cn(
    "inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-semibold transition-colors",
    FOCUS_RING,
    tab === t ? "bg-brand-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100",
  );

  const balanceColumns = [
    { key: "produto", header: "Produto", cell: ({ p, first }) => first ? <span className="text-slate-900 font-medium">{p.name}</span> : null },
    { key: "variacao", header: "Variação", cell: ({ v }) => <span className="text-slate-600">{v.label}</span> },
    { key: "barcode", header: "Código de barras", mono: true, className: "text-xs text-slate-500", cell: ({ v }) => v.barcode || "—" },
    // Uma coluna só: o saldo deixou de ser dividido entre site e balcão. Antes
    // eram três (Online, Física, Geral) para descrever uma prateleira só.
    {
      key: "saldo", header: "Em estoque", align: "right",
      cell: ({ p, v }) => (
        <Scale value={v.stock} max={SCALE_MAX} label={`${p.name} ${v.label}: ${v.stock} em estoque`} />
      ),
    },
  ];

  // Comprovante da movimentação em bobina. A movimentação já existe quando o
  // botão aparece, então a impressão nunca inventa um registro que não foi
  // gravado — ela só dá corpo de papel ao que está no extrato.
  const imprimirMovimento = async (m) => {
    const tid = toast.loading("Enviando comprovante para a impressora...");
    try {
      await printAgent.printReceipt(buildStockMovementHtml(m), { label: `Estoque ${m.product_name || ""}` });
      toast.success("Comprovante enviado", { id: tid });
    } catch (e) {
      toast.error(e.message || "Falha ao imprimir", { id: tid });
    }
  };

  const movColumns = [
    { key: "data", header: "Data", className: "text-slate-500 whitespace-nowrap", cell: (m) => new Date(m.created_at).toLocaleString("pt-BR") },
    { key: "produto", header: "Produto", className: "text-slate-900", cardTitle: true, cell: (m) => m.product_name || m.product_id },
    { key: "variacao", header: "Variação", className: "text-slate-600", cell: (m) => m.variant_label || "—" },
    { key: "canal", header: "Canal", className: "text-xs text-slate-500", cell: (m) => LOC_LABEL[m.location] || "—" },
    { key: "tipo", header: "Tipo", cell: (m) => <Badge tone={TYPE_TONE[m.type]}>{TYPE_LABEL[m.type] || m.type}</Badge> },
    { key: "qtd", header: "Qtd", align: "right", mono: true, cell: (m) => m.qty },
    { key: "motivo", header: "Motivo", className: "text-slate-500", cell: (m) => m.reason || "—" },
    {
      // Na LINHA, e não só no lançamento: a conferência costuma pedir o papel
      // depois, e reimprimir uma movimentação de ontem tem que ser possível.
      key: "acoes", header: "", align: "right", width: "3.5rem",
      cell: (m) => (
        <Button variant="ghost" size="iconSm" onClick={() => imprimirMovimento(m)}
          data-testid="stock-movement-print" title="Imprimir comprovante"
          aria-label={`Imprimir comprovante da movimentação de ${m.product_name || "produto"}`}><Printer size={15} /></Button>
      ),
    },
  ];

  return (
    <div className="space-y-5" data-testid="admin-stock-page">
      <PageHeader
        eyebrow="Operação"
        title="Estoque"
        description="Saldo por canal e histórico de movimentações. Loja física e online compartilham o mesmo cadastro, com contagem separada."
      />

      {/* Lançamento de movimentação */}
      <Card>
        <CardBody>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-7 gap-3 items-end">
            <Field label="Produto e variação" className="md:col-span-3">
              <ProductPicker
                productId={form.product_id}
                variantId={form.variant_id}
                onChange={selectProduct}
                testId="stock-product"
              />
            </Field>
            <Field label="Canal">
              <Select value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="stock-location-select">
                <option value="fisica">Loja física</option>
                <option value="online">Loja online</option>
              </Select>
            </Field>
            <Field label="Tipo">
              <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="stock-type-select">
                <option value="entrada">Entrada</option>
                <option value="saida">Saída</option>
                <option value="ajuste">Ajuste (inventário)</option>
              </Select>
            </Field>
            <Field
              label={form.type === "ajuste" ? "Novo saldo" : "Qtd"}
              hint={form.type === "ajuste" ? "Substitui o saldo atual" : undefined}
            >
              <Input type="number" min="0" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="stock-qty-input" mono />
            </Field>
            <Button type="submit" loading={busy} icon={PackagePlus} data-testid="stock-submit-btn">
              {busy ? "Registrando..." : "Registrar"}
            </Button>
            <Field label="Motivo" className="md:col-span-7">
              <Input
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="Ex.: compra fornecedor, avaria, contagem de inventário"
                data-testid="stock-reason-input"
              />
            </Field>
          </form>
        </CardBody>
      </Card>

      {/* Abas + busca */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1.5 bg-white border border-slate-200 rounded-xl p-1.5 w-fit">
          <button type="button" onClick={() => setTab("saldo")} data-testid="stock-tab-balance"
            aria-pressed={tab === "saldo"} data-ring="" className={tabBtn("saldo")}>
            <Boxes size={16} aria-hidden /> Saldo atual
          </button>
          <button type="button" onClick={() => setTab("movimentos")} data-testid="stock-tab-movements"
            aria-pressed={tab === "movimentos"} data-ring="" className={tabBtn("movimentos")}>
            <History size={16} aria-hidden /> Movimentações
            <span className="text-xs opacity-70 font-mono">{movs.total}</span>
          </button>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch("")}
            placeholder="Filtrar produto..."
            aria-label="Filtrar produto"
            width="w-56"
            data-testid="stock-search-input"
          />
          {tab === "movimentos" && (
            <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
              data-testid="stock-type-filter" aria-label="Filtrar por tipo" className="w-auto text-slate-600">
              <option value="">Todos tipos</option>
              <option value="entrada">Entrada</option>
              <option value="saida">Saída</option>
              <option value="ajuste">Ajuste</option>
            </Select>
          )}
        </div>
      </div>

      {tab === "saldo" && (
        <>
          <DataTable
            caption="Saldo de estoque por produto e variação"
            columns={balanceColumns}
            rows={balanceRows}
            rowKey={({ v }) => v.id}
            loading={balance.isLoading}
            error={balance.isError}
            onRetry={balance.refetch}
            empty={{
              icon: Boxes,
              title: term ? `Nenhum produto para "${term}"` : "Nenhum produto cadastrado",
              desc: term ? "Ajuste o filtro de busca." : "Cadastre um produto em Produtos para ele aparecer aqui.",
              testId: "stock-balance-empty",
            }}
          />
          <Pagination page={balance.page} lastPage={balance.lastPage} total={balance.total} onChange={balance.setPage} label="produtos" />
        </>
      )}

      {tab === "movimentos" && (
        <>
          <DataTable
            caption="Histórico de movimentações de estoque"
            columns={movColumns}
            rows={movs.items}
            rowKey={(m) => m.id}
            loading={movs.isLoading}
            error={movs.isError}
            onRetry={movs.refetch}
            empty={{
              icon: History,
              title: "Nenhuma movimentação",
              desc: "Entradas, saídas e ajustes de inventário aparecem aqui assim que forem registrados no formulário acima.",
              testId: "stock-movements-empty",
            }}
          />
          <Pagination page={movs.page} lastPage={movs.lastPage} total={movs.total} onChange={movs.setPage} label="movimentações" />
        </>
      )}
    </div>
  );
}
